import { useImmer } from "use-immer";
import { useEffect, useMemo, useState } from "react";
import { faCircleQuestion } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const Component = ({ config, generalConfig, onChange }) => {
  const [data, updateData] = useImmer(config);
  const [open, setOpen] = useState(false);
  const [tiersOpen, setTiersOpen] = useState(false);

  useEffect(() => {
    updateData(config);
  }, [config]);

  useEffect(() => {
    onChange(data);
  }, [data]);

  const totalSlots = generalConfig.totalSlots;
  const slotSizeDays = generalConfig.slotSizeDays;

  // auto recalculation - every time when there is a change in the Tier config
  const example = useMemo(() => {
    if (!totalSlots || totalSlots < 1) return [];

    const validSlotsSamples = Array.from({ length: totalSlots + 1 }, (_, i) =>
      Number(((i / totalSlots) * 100).toFixed(1)),
    );

    return validSlotsSamples.map((r) => {
      const hits = Math.min(totalSlots, Math.round((r / 100) * totalSlots));
      const factor = hits / totalSlots;

      return {
        r,
        hits,
        factor,
        tier1: factor * data.tier1Score,
        tier2: factor * data.tier2Score,
        tier3: factor * data.tier3Score,
      };
    });
  }, [totalSlots, data.tier1Score, data.tier2Score, data.tier3Score]);

  return (
    <>
      {/* ---------------- TIERS DEFINITIONS HEADER ---------------- */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          marginBottom: "10px",
          marginTop: "10px",
          cursor: "pointer",
          userSelect: "none",
          width: "fit-content",
        }}
        onClick={() => setTiersOpen(!tiersOpen)}
        title={tiersOpen ? "Hide tier definitions" : "Show tier definitions"}
      >
        <strong>Tiers definitions</strong>

        <FontAwesomeIcon
          icon={faCircleQuestion}
          style={{ fontSize: "18px", opacity: 0.8 }}
        />
      </div>

      {/* ---------------- COLLAPSIBLE PANEL ---------------- */}
      {tiersOpen && (
        <div
          style={{
            marginBottom: "20px",
            padding: "12px",
            background: "#f7f7f7",
            border: "1px solid #ddd",
            borderRadius: "6px",
          }}
        >
          <strong>How tiers work</strong>

          <p style={{ marginTop: "6px" }}>
            Patterns are grouped into three tiers based on their quality. Higher tiers
            represent stronger, more meaningful and more consistent customer behavior.
          </p>

          <ul style={{ marginTop: "6px", paddingLeft: "20px" }}>
            <li>
              <strong>Tier 1:</strong> customer uses the same location regularly
              (at least 5 transactions at the same merchant within <strong>{slotSizeDays}</strong> days).
            </li>

            <li style={{ marginTop: "4px" }}>
              <strong>Tier 2:</strong> same as Tier 1, but at least <strong>80%</strong> of these
              transactions occur within the same 1-hour time slot.
            </li>

            <li style={{ marginTop: "4px" }}>
              <strong>Tier 3:</strong> same as Tier 1, but the transactions at this location
              make up at least <strong>10%</strong> of all customer transactions in the same <strong>{slotSizeDays}</strong>-day period.
            </li>
          </ul>

          {/* --- NEW NOTE --- */}
          <p style={{ marginTop: "10px", fontSize: "12px", color: "#555" }}>
            Note: The number of slots and the size of each slot are configurable
            in the admin section. Values shown above reflect your current configuration.
          </p>
        </div>
      )}

      {/* ---------------- COLLAPSIBLE HEADER ---------------- */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          marginBottom: "10px",
          marginTop: "10px",
          cursor: "pointer",
          userSelect: "none",
          width: "fit-content",
        }}
        onClick={() => setOpen(!open)}
        title={open ? "Hide scoring details" : "Show scoring details"}
      >
        <strong>Scoring details</strong>

        <FontAwesomeIcon
          icon={faCircleQuestion}
          style={{ fontSize: "18px", opacity: 0.8, cursor: "pointer" }}
        />
      </div>

      {/* ---------------- COLLAPSIBLE PANEL ---------------- */}
      {open && (
        <div
          style={{
            marginBottom: "20px",
            padding: "12px",
            background: "#f7f7f7",
            border: "1px solid #ddd",
            borderRadius: "6px",
          }}
        >
          <strong>How scoring works</strong>

          <p style={{ marginTop: "6px" }}>
            Relevance determines how many slots contribute to the final score.
            Your current configuration uses <strong>{totalSlots}</strong> slots.
          </p>

          <p style={{ marginTop: "6px" }}>
            The table below shows how relevance is converted into score for each
            tier. Values are calculated live based on your current settings.
          </p>

          <p style={{ marginTop: "6px" }}>
            Adjust the <strong>Tier 1 / Tier 2 / Tier 3</strong> scores below
            and the table will update automatically.
          </p>
          {/* --- MINI TABULKA --- */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "12px",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th>Relevance</th>
                <th>Slot (Hits)</th>
                {/*<th>Factor</th>*/}
                <th>Tier1</th>
                <th>Tier2</th>
                <th>Tier3</th>
              </tr>
            </thead>

            <tbody>
              {example
                .filter((row) => row.hits > 0)
                .map((row) => (
                  <tr key={row.r}>
                    <td>{row.r}%</td>
                    <td>{row.hits}</td>
                    {/*<td>{row.factor.toFixed(3)}</td>*/}
                    <td>{row.tier1.toFixed(2)}</td>
                    <td>{row.tier2.toFixed(2)}</td>
                    <td>{row.tier3.toFixed(2)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      <table className="config-table">
        <thead>
          <tr>
            <th>Attribute</th>
            <th>Score</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Tier 1</td>
            <td>
              <input
                type="number"
                value={data.tier1Score}
                onChange={(e) =>
                  updateData((d) => {
                    d.tier1Score = e.target.value;
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td>Tier 2</td>
            <td>
              <input
                type="number"
                value={data.tier2Score}
                onChange={(e) =>
                  updateData((d) => {
                    d.tier2Score = e.target.value;
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td>Tier 3</td>
            <td>
              <input
                type="number"
                value={data.tier3Score}
                onChange={(e) =>
                  updateData((d) => {
                    d.tier3Score = e.target.value;
                  })
                }
              />
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default Component;